import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton } from "@shared/CustomButton";
import PasswordField from "@shared/form/PasswordField";
import { TextField } from "@shared/form/TextField";
import { Modal } from "@shared/Modal/Modal";

import { setBwcCredentialsValidationSchema } from "./SetBwcCredentialsDialog.validation";

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
}));

export type BwcCredentialsFormValues = {
    bwcAccount: string;
    bwcSecret: string;
    bwcSecretRepeat: string;
};

type Props = {
    open: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (values: BwcCredentialsFormValues) => Promise<void>;
};

export const SetBwcCredentialsDialog = ({ open, loading, onClose, onSubmit }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);
    const validate = useValidationSchema(setBwcCredentialsValidationSchema);

    const handleSubmit = async (values: BwcCredentialsFormValues, form: FormApi<BwcCredentialsFormValues>) => {
        try {
            await onSubmit(values);
        } catch {
            // The caller shows its own error message. Catching here stops a rejected onSubmit
            // from becoming an unhandled rejection inside form.submit().
        } finally {
            // Never keep the credentials in form state, not even after a failed request.
            form.restart();
        }
    };

    return (
        <Form<BwcCredentialsFormValues>
            onSubmit={handleSubmit}
            validate={validate}
            render={({ handleSubmit: submitForm, form, submitting }) => (
                <Modal
                    open={open}
                    onClose={() => {
                        if (loading) {
                            return;
                        }
                        form.restart();
                        onClose();
                    }}
                    title={t("sidepanels:devicePanel.bwcTab.credentials.title")}
                    description={t("sidepanels:devicePanel.bwcTab.credentials.description")}
                    secondaryActionText={t("common:button.cancel")}
                    endButton={
                        <PrimaryButton onClick={form.submit} disabled={submitting || loading} loading={submitting || loading}>
                            {t("common:button.confirm")}
                        </PrimaryButton>
                    }
                >
                    {/*
                        These credentials belong to the device WebUI, not to the Hub. Chromium ignores
                        autocomplete="off" on credential fields and falls back to heuristics, so the field
                        names deliberately avoid "username"/"password" and the tokens below are the opt-outs
                        that do work: "new-password" for the passwords, an unrecognized token for the account.
                    */}
                    <form onSubmit={submitForm} className={classes.form}>
                        <TextField
                            name="bwcAccount"
                            label={t("sidepanels:devicePanel.bwcTab.credentials.usernameLabel")}
                            autoComplete="bwc-account-field"
                            fullWidth
                        />
                        <PasswordField
                            name="bwcSecret"
                            label={t("sidepanels:devicePanel.bwcTab.credentials.passwordLabel")}
                            autoComplete="new-password"
                            fullWidth
                        />
                        <PasswordField
                            name="bwcSecretRepeat"
                            label={t("sidepanels:devicePanel.bwcTab.credentials.passwordRepeatLabel")}
                            autoComplete="new-password"
                            fullWidth
                        />
                    </form>
                    <CustomAlert severity="info">
                        <Trans
                            ns="sidepanels"
                            i18nKey="devicePanel.bwcTab.credentials.warning"
                            components={{ bold: <strong /> }}
                        />
                    </CustomAlert>
                </Modal>
            )}
        />
    );
};
